import React, { useMemo, useCallback, memo, useState } from 'react';
import { Box, HStack, Icon, Text, Menu, MenuButton, MenuList, MenuItem, Spinner, VStack, Divider, Button, IconButton } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FaTrash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import { useBellNotifications } from '../hooks/useBellNotifications';

interface NotificationMenuProps {
  notificationCount?: number;
}

// Memoize the notification item to prevent unnecessary re-renders
const NotificationItem = memo(({ notification, onMarkAsRead, onNavigate, onDelete }: {
  notification: any;
  onMarkAsRead: (id: number) => void;
  onNavigate: (url: string) => void;
  onDelete: (id: number) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = useCallback(() => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
    if (notification.linkUrl) {
      onNavigate(notification.linkUrl);
    }
  }, [notification, onMarkAsRead, onNavigate]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  }, [notification.id, onDelete]);

  return (
    <MenuItem
      bg={notification.is_read ? "transparent" : "rgba(255, 255, 255, 0.05)"}
      color="white"
      _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
      _focus={{ bg: 'rgba(255, 255, 255, 0.1)' }}
      py={3}
      px={4}
      onClick={handleClick}
      cursor={notification.linkUrl ? "pointer" : "default"}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      position="relative"
    >
      <VStack align="start" spacing={1} w="full">
        {!notification.is_read && (
          <Text 
            fontSize="xs" 
            color="red.400" 
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            New
          </Text>
        )}
        <Text 
          fontSize="sm" 
          color={notification.is_read ? "gray.300" : "white"}
          fontWeight={notification.is_read ? "normal" : "medium"}
          lineHeight="1.4"
        >
          {notification.processedMessage}
        </Text>
        <Text fontSize="xs" color="gray.500">
          {notification.formattedDate}
        </Text>
      </VStack>
      {isHovered && (
        <IconButton
          aria-label="Delete notification"
          icon={<FaTrash />}
          size="xs"
          variant="ghost"
          color="red.400"
          _hover={{ bg: 'rgba(255, 0, 0, 0.1)', color: 'red.300' }}
          position="absolute"
          right={2}
          top="50%"
          transform="translateY(-50%)"
          onClick={handleDelete}
          zIndex={1}
        />
      )}
    </MenuItem>
  );
});

NotificationItem.displayName = 'NotificationItem';

export const NotificationMenu: React.FC<NotificationMenuProps> = ({ 
  notificationCount: propNotificationCount 
}) => {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, error, markAsRead, markAllAsRead, deleteNotification } = useBellNotifications();
  
  // Use unreadCount from hook if no prop is provided
  const notificationCount = propNotificationCount ?? unreadCount;

  // Memoize formatted dates and limit to most recent 50 notifications for performance
  const formattedNotifications = useMemo(() => {
    return notifications
      .slice(0, 50) // Only show the 50 most recent notifications
      .map(notification => ({
        ...notification,
        formattedDate: new Date(notification.created_at).toLocaleDateString()
      }));
  }, [notifications]);

  const handleMarkAsRead = useCallback((id: number) => {
    markAsRead(id);
  }, [markAsRead]);

  const handleNavigate = useCallback((url: string) => {
    navigate(url);
  }, [navigate]);

  const handleDelete = useCallback((id: number) => {
    deleteNotification(id);
  }, [deleteNotification]);

  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <Menu 
      closeOnBlur={true}
      closeOnSelect={false}
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
    >
      <MenuButton
        as={Box}
        sx={{
          background: 'rgba(0, 46, 11, 0.2)',
        }}
        borderRadius="full"
        p={2}
        boxShadow="0 4px 6px rgba(0, 0, 0, 0.3)"
        cursor="pointer"
        _hover={{ 
          background: 'rgba(0, 46, 11, 0.3)',
        }}
        transition="background 0.15s"
        position="relative"
      >
        <NotificationBell notificationCount={notificationCount} />
        <Box
          position="absolute"
          bottom="0"
          right="0"
          bg="rgba(0, 46, 11, 0.8)"
          borderRadius="full"
          w="16px"
          h="16px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          border="1px solid"
          borderColor="rgba(255, 255, 255, 0.1)"
        >
          <Icon
            as={ChevronDownIcon}
            color="white"
            opacity={0.7}
            w={2}
            h={2}
            transition="transform 0.2s"
          />
        </Box>
      </MenuButton>
      <MenuList
        bg="rgba(0, 0, 0, 0.95)"
        border="1px solid"
        borderColor="rgba(255, 255, 255, 0.2)"
        borderRadius="lg"
        boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)"
        minW="300px"
        maxW="400px"
        maxH="400px"
        overflowY="auto"
        zIndex={1001}
      >
        {loading ? (
          <Box p={4} display="flex" justifyContent="center" alignItems="center">
            <Spinner color="white" size="sm" />
            <Text ml={2} fontSize="sm" color="white">Loading notifications...</Text>
          </Box>
        ) : error ? (
          <Box p={4}>
            <Text fontSize="sm" color="red.300">
              Error loading notifications
            </Text>
          </Box>
        ) : formattedNotifications.length === 0 ? (
          <MenuItem
            bg="transparent"
            color="white"
            _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
            _focus={{ bg: 'rgba(255, 255, 255, 0.1)' }}
            py={3}
            px={4}
            disabled
          >
            <Text fontSize="sm" color="gray.400">
              No notifications
            </Text>
          </MenuItem>
        ) : (
          <>
            <Box px={4} py={2} borderBottom="1px solid" borderColor="rgba(255, 255, 255, 0.1)">
              <HStack justify="space-between" align="center">
                {notifications.length > formattedNotifications.length ? (
                  <Text fontSize="xs" color="gray.400" textAlign="left">
                    Showing {formattedNotifications.length} of {notifications.length} notifications
                  </Text>
                ) : (
                  <Box />
                )}
                <Button
                  size="xs"
                  colorScheme="green"
                  bg="#002e0b"
                  color="white"
                  _hover={{ bg: '#004d1a' }}
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/notifications');
                  }}
                >
                  VIEW ALL
                </Button>
              </HStack>
            </Box>
            {formattedNotifications.length > 1 && (
              <>
                <MenuItem
                  bg="transparent"
                  color="gray.300"
                  _hover={{ bg: 'rgba(255, 255, 255, 0.05)' }}
                  _focus={{ bg: 'rgba(255, 255, 255, 0.05)' }}
                  py={2}
                  px={4}
                  onClick={markAllAsRead}
                >
                  <Text fontSize="xs" fontWeight="medium">
                    Mark all as read
                  </Text>
                </MenuItem>
                <Divider borderColor="rgba(255, 255, 255, 0.1)" />
              </>
            )}
            {formattedNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onNavigate={handleNavigate}
                  onDelete={handleDelete}
                />
                {index < formattedNotifications.length - 1 && (
                  <Divider borderColor="rgba(255, 255, 255, 0.1)" />
                )}
              </React.Fragment>
            ))}
          </>
        )}
      </MenuList>
    </Menu>
  );
};

export default NotificationMenu;
